import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import { campaignService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { ASSETS, getImageUrl } from '../utils/assets';
import { getCampaignPath } from '../utils/campaignUrl';
import DonateNowButton from '../components/DonateNowButton';
import HomeHeroSlider from '../components/HomeHeroSlider';
import HomePopup from '../components/HomePopup';
import SiteStatsItems from '../components/SiteStatsItems';

const DARK_GREEN = '#1a3d1a';
const BROWN_BTN = '#7a5c1b';

function PlusIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function HeartIcon({ size = 40, color = '#d4a574' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function ArrowBackIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  );
}

function ArrowForwardIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
    </svg>
  );
}

// Alias for any cached/hot-reload reference
const AddIcon = PlusIcon;

// Fallback events when API returns none (Figma image-14 style)
const FALLBACK_UPCOMING_EVENTS = [
  { title: 'Sponsor a Medical Camp', date: 'Feb 20,2026', img: ASSETS.cause1 },
  { title: 'Sponsor a Medical Camp', date: 'March 20,2026', img: ASSETS.cause4 },
  { title: 'Sponsor a Medical Camp', date: 'Apr 22,2026', img: ASSETS.cause6 },
];

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'March', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()},${d.getFullYear()}`;
}

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const eventsScrollRef = useRef(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await campaignService.getAllCampaigns({ status: 'active' });
        const data = response.data?.data || response.data || [];
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const displayCampaigns = campaigns.length > 0 ? campaigns.slice(0, 4) : [
    { id: 1, title: 'Education Need', description: 'Support children with books and school supplies.', target_amount: 20000, current_amount: 16000, image_url: ASSETS.cause1 },
    { id: 2, title: 'Water Wells', description: 'Clean water for villages.', target_amount: 15000, current_amount: 7500, image_url: ASSETS.cause4 },
    { id: 3, title: 'Meal Support For Kids', description: 'Daily meals for underprivileged children.', target_amount: 10000, current_amount: 6000, image_url: ASSETS.cause6 },
    { id: 4, title: 'Healthcare for Communities', description: 'Free medical camps and health awareness.', target_amount: 12000, current_amount: 5000, image_url: ASSETS.rectangle },
  ];

  const upcomingEventsList =
    campaigns.length > 0
      ? campaigns.map((c) => ({
          id: c.id,
          slug: c.slug,
          title: c.title,
          date: formatEventDate(c.start_date || c.end_date || c.created_at),
          img: getImageUrl(c.image_url) || ASSETS.rectangle,
        }))
      : FALLBACK_UPCOMING_EVENTS;

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <HomePopup />
      <HomeHeroSlider />
      <Box sx={{ height: 4, backgroundColor: '#EEECE1', width: '100%' }} />

      {/* Our Mission - Figma: left = heart (sprade-base) + image-08 495×556 radius 12px br + parallax 424×282 border 10px #F9F4E8; right = Caveat/Nunito/Poppins + More About Us 162×50 radius 28 #477C2E */}
      <Box sx={{ backgroundColor: { xs: '#fff', md: '#FAF9F6' }, py: { xs: 6, md: 10 }, overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 3 }, boxSizing: 'border-box', overflow: 'hidden' }}>
          <Grid container alignItems="flex-start" spacing={{ xs: 0, md: 6 }} sx={{ width: '100%', maxWidth: '100%' }}>
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <Box sx={{ position: 'relative', display: 'flex', mb: { xs: 3, md: 0 }, alignItems: 'flex-start', justifyContent: { xs: 'center', md: 'flex-start' }, width: { xs: '100%', md: 'auto' } }}>
                <Box
                  component="img"
                  src={ASSETS.spradeBase}
                  alt=""
                  sx={{ width: { xs: 70, sm: 95 }, height: { xs: 104, sm: 141 }, flexShrink: 0, ml: { xs: 0, md: -1 }, mt: { xs: 1, md: 2 }, position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' } }}
                />
                {/* Figma: image-08 495×556; on mobile single image only; desktop overflow visible so second photo shows full */}
                <Box sx={{ position: 'relative', flex: 1, maxWidth: { xs: '100%', md: 495 }, width: { xs: '100%', md: 'auto' }, overflow: 'hidden', minWidth: 0 }}>
                  <Box
                    component="img"
                    src={ASSETS.image08}
                    alt="Child with water"
                    sx={{
                      width: '100%',
                      maxWidth: { xs: '100%', md: 495 },
                      height: 'auto',
                      maxHeight: { xs: 400, md: 556 },
                      objectFit: 'cover',
                      borderBottomRightRadius: 12,
                      display: 'block',
                      verticalAlign: 'top',
                    }}
                  />
                  <Box
                    component="img"
                    src={ASSETS.parallax}
                    alt="Hands with water"
                    sx={{
                      position: 'absolute',
                      left: { xs: 0, md: -39 },
                      top: { md: '60%' },
                      width: { xs: '85%', sm: '90%', md: 424 },
                      maxWidth: { md: 424 },
                      height: { xs: 'auto', md: 282 },
                      minHeight: { md: 282 },
                      maxHeight: { xs: 180, md: 282 },
                      objectFit: 'cover',
                      border: { xs: '6px solid #F9F4E8', md: '10px solid #F9F4E8' },
                      boxSizing: 'border-box',
                      zIndex: 2,
                      display: { xs: 'none', md: 'block' },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
              <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                <Typography
                  sx={{
                    fontFamily: '"Caveat", cursive',
                    fontWeight: 700,
                    fontSize: 27,
                    lineHeight: '1.25em',
                    letterSpacing: 0,
                    color: '#313530',
                    mb: '25px',
                  }}
                >
                  Our Mission.
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Nunito", sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: 28, md: 40 },
                    lineHeight: '1.25em',
                    letterSpacing: '0.01em',
                    color: '#2D2929',
                    mb: '50px',
                    maxWidth: { xs: '100%', md: 540 },
                  }}
                >
                  Helping each other can make world better
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 300,
                    fontSize: 14,
                    lineHeight: { xs: 1.6, md: '43px' },
                    letterSpacing: '0.04em',
                    color: '#4B4646',
                    mb: { xs: 4, md: 5 },
                    maxWidth: { xs: '100%', md: 575 },
                    width: { xs: '100%', md: 'auto' },
                    overflowWrap: { xs: 'break-word', md: 'normal' },
                    wordBreak: { xs: 'break-word', md: 'normal' },
                    boxSizing: 'border-box',
                  }}
                >
                  Roots & Wings is committed to creating positive social impact through community engagement, education, and sustainable development initiatives. We work towards empowering individuals and communities to achieve their full potential.
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => navigate('/about')}
                  sx={{
                    width: { xs: '100%', sm: 162 },
                    maxWidth: { xs: '100%', sm: 'none' },
                    minWidth: { xs: 0, sm: 'auto' },
                    height: 50,
                    borderRadius: 28,
                    backgroundColor: '#477C2E',
                    color: '#FFFFFF',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 400,
                    fontSize: 15,
                    lineHeight: '100%',
                    letterSpacing: '0.02em',
                    boxSizing: 'border-box',
                    '&:hover': { backgroundColor: '#3d6a27' },
                  }}
                >
                  More About Us
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Upcoming Events - Figma: cream bg, left text, right = single images with horizontal scroll */}
      <Box
        sx={{
          backgroundColor: '#F9F4E8',
          py: { xs: 6, md: 8 },
          minHeight: { xs: 'auto', md: 578 },
          display: 'flex',
          alignItems: 'center',
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, md: 3 }, boxSizing: 'border-box', overflowX: 'hidden' }}>
          <Grid container spacing={{ xs: 0, md: 4 }} alignItems="center" sx={{ width: '100%', maxWidth: '100%' }}>
            <Grid item xs={12} md={4} sx={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden', boxSizing: 'border-box', mb: { xs: 3, md: 0 } }}>
              <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                <Typography
                  sx={{
                    fontFamily: '"Caveat", cursive',
                    fontWeight: 700,
                    fontSize: { xs: 24, md: 27 },
                    lineHeight: '1.25em',
                    letterSpacing: 0,
                    color: '#313530',
                    mb: '25px',
                  }}
                >
                  Upcoming events
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Nunito", sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: 28, sm: 34, md: 38 },
                    lineHeight: '1.25em',
                    letterSpacing: '0.01em',
                    color: '#2D2929',
                    mb: { xs: '30px', md: '50px' },
                    maxWidth: { xs: '100%', md: 351 },
                  }}
                >
                  Join Our Latest Upcoming events
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 300,
                    fontSize: 14,
                    lineHeight: { xs: 1.6, md: '38px' },
                    letterSpacing: '0.04em',
                    color: '#4B4646',
                    maxWidth: { xs: '100%', md: 378 },
                    width: { xs: '100%', md: 'auto' },
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    boxSizing: 'border-box',
                  }}
                >
                  Join our campaigns and events to support education, health, and community development. Your participation helps create lasting change for children and families in need.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  ref={eventsScrollRef}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    pb: 1,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {upcomingEventsList.map((ev, i) => (
                    <Box
                      key={ev.id || i}
                      onClick={() => navigate(ev.slug ? getCampaignPath(ev.slug) : '/campaigns')}
                      sx={{
                        flex: '0 0 auto',
                        width: { xs: 280, sm: 320 },
                        maxWidth: '85vw',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
                      }}
                    >
                      <Box sx={{ position: 'relative', height: { xs: 260, sm: 300, md: 332 } }}>
                        <Box
                          component="img"
                          src={getImageUrl(ev.img) || ASSETS.rectangle}
                          alt={ev.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        {/* Date tag - Figma: 93×30, radius 15px, #FFDF6A; text Caveat 700 17px #313530 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 93,
                            height: 30,
                            borderRadius: '15px',
                            backgroundColor: '#FFDF6A',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: '"Caveat", cursive',
                              fontWeight: 700,
                              fontSize: 17,
                              lineHeight: '100%',
                              letterSpacing: 0,
                              color: '#313530',
                            }}
                          >
                            {ev.date}
                          </Typography>
                        </Box>
                        {/* Title overlay - Figma: Nunito 900 20px line-height 33px letter-spacing 4% #FFFFFF */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                            py: 2,
                            px: 2,
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: '"Nunito", sans-serif',
                              fontWeight: 900,
                              fontSize: 20,
                              lineHeight: '33px',
                              letterSpacing: '0.04em',
                              color: '#FFFFFF',
                            }}
                          >
                            {ev.title}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Button
                  aria-label="Scroll events back"
                  onClick={() => {
                    eventsScrollRef.current?.scrollBy({ left: -340, behavior: 'smooth' });
                  }}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    minWidth: 51,
                    width: 51,
                    height: 51,
                    borderRadius: '50%',
                    backgroundColor: '#477C2E',
                    color: '#fff',
                    zIndex: 2,
                    '&:hover': { backgroundColor: '#3d6a27' },
                  }}
                >
                  <ArrowBackIcon size={20} />
                </Button>
                <Button
                  aria-label="Scroll events forward"
                  onClick={() => {
                    eventsScrollRef.current?.scrollBy({ left: 340, behavior: 'smooth' });
                  }}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    minWidth: 51,
                    width: 51,
                    height: 51,
                    borderRadius: '50%',
                    backgroundColor: '#477C2E',
                    color: '#fff',
                    zIndex: 2,
                    '&:hover': { backgroundColor: '#3d6a27' },
                  }}
                >
                  <ArrowForwardIcon size={20} />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Popular Cause and Donate - Figma: Help & donate us now; 3 cards white + shadow; Ellipse 51px #477C2E 2px white border; GOAL/RAISED; DONATE NOW */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              fontWeight: 700,
              fontSize: { xs: 24, md: 27 },
              lineHeight: '1.25em',
              letterSpacing: 0,
              color: '#313530',
              textAlign: 'center',
              mb: '25px',
            }}
          >
            Help & donate us now
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 26, sm: 34, md: 40 },
              lineHeight: '1.25em',
              letterSpacing: '0.01em',
              color: '#2D2929',
              textAlign: 'center',
              mb: { xs: '30px', md: '50px' },
            }}
          >
            Find the popular cause
            <br />
            and donate them
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: DARK_GREEN }} />
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {displayCampaigns.map((c) => {
                const target = Number(c.target_amount) || 10000;
                const raised = Number(c.current_amount ?? c.raised_amount) || 0;
                const pct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;
                return (
                  <Grid item xs={12} sm={6} md={3} key={c.id}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 0 17px 0 rgba(0,0,0,0.06)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: '0 0 24px 0 rgba(0,0,0,0.1)' },
                      }}
                      onClick={() => (c.slug ? navigate(getCampaignPath(c.slug)) : navigate('/campaigns'))}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="214"
                          image={getImageUrl(c.image_url) || ASSETS.rectangle}
                          alt={c.title}
                          sx={{ objectFit: 'cover' }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            width: 51,
                            height: 51,
                            borderRadius: '50%',
                            backgroundColor: '#477C2E',
                            border: '2px solid #FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          <ArrowForwardIcon size={20} />
                        </Box>
                      </Box>
                      <CardContent sx={{ flexGrow: 1, px: { xs: 2, md: 3 }, pt: 2.5, pb: 2, textAlign: 'left' }}>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: 17, md: 19 },
                            lineHeight: 1.3,
                            letterSpacing: '1px',
                            color: '#3F3535',
                            mb: 1.5,
                            textAlign: 'left',
                          }}
                        >
                          {c.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 300,
                            fontSize: 14,
                            lineHeight: 1.6,
                            letterSpacing: '0.04em',
                            color: '#4B4646',
                            mb: 1.5,
                            textAlign: 'left',
                          }}
                        >
                          {typeof c.description === 'string' && c.description.trim() ? c.description : 'Support this cause and help create lasting change in our community. Every contribution makes a difference.'}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            mb: 2,
                            '& .MuiLinearProgress-bar': { backgroundColor: BROWN_BTN },
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 1.5,
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 400,
                            fontSize: 14,
                            lineHeight: 1.4,
                            letterSpacing: '0.04em',
                            color: '#4C4040',
                            textAlign: 'left',
                          }}
                        >
                          <span>GOAL : {target?.toLocaleString?.() ?? target} $</span>
                          <span>RAISED : {raised?.toLocaleString?.() ?? raised} $</span>
                        </Box>
                      </CardContent>
                      <Box sx={{ p: { xs: 2, md: 3 }, pt: 2, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, textAlign: 'right' }}>
                        <DonateNowButton
                          size="small"
                          to={c.slug ? getCampaignPath(c.slug) : '/campaigns'}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(c.slug ? getCampaignPath(c.slug) : '/campaigns');
                          }}
                          sx={{ minWidth: { xs: '100%', sm: 120 }, borderRadius: 9999 }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Become a Volunteer - Figma: 1281×475; overlay #1C1C1C 55%; Caveat/Nunito/Poppins; Join With Us 146×50 radius 28 #477C2E; bottom shape */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 380, md: 475 },
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: `url(${ASSETS.parallax})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', sm: 'fixed' },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(28, 28, 28, 0.55)',
          },
        }}
      >
        <Container maxWidth={false} sx={{ position: 'relative', zIndex: 1, width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 5, md: 6 }, flex: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ maxWidth: 494, pl: { xs: 0, sm: '49px' }, width: '100%' }}>
            <Typography
              sx={{
                fontFamily: '"Caveat", cursive',
                fontWeight: 700,
                fontSize: { xs: 24, md: 27 },
                lineHeight: '100%',
                letterSpacing: 0,
                color: '#FFFFFF',
                mb: 2,
              }}
            >
              Help & Join with us now
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Nunito", sans-serif',
                fontWeight: 900,
                fontSize: { xs: 28, sm: 34, md: 40 },
                lineHeight: '100%',
                letterSpacing: '1px',
                color: '#FFFFFF',
                mb: 2.5,
              }}
            >
              Become a Volunteer
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '35px',
                letterSpacing: '0.04em',
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Join our volunteer network and give your time and skills to support education, health camps, and community programs. Together we can build a better tomorrow for those who need it most.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/volunteer')}
              sx={{
                width: { xs: '100%', sm: 146 },
                height: 50,
                borderRadius: 28,
                backgroundColor: '#477C2E',
                color: '#FFFFFF',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: 14,
                lineHeight: '100%',
                letterSpacing: '0.02em',
                '&:hover': { backgroundColor: '#3d6a27' },
              }}
            >
              Join With Us
            </Button>
          </Box>
        </Container>
        <Box sx={{ width: '100%', lineHeight: 0, flexShrink: 0 }}>
          <Box
            component="img"
            src={ASSETS.volunteerShape || ASSETS.bannerBottom}
            alt=""
            sx={{
              width: '100%',
              height: { xs: 50, md: 71 },
              objectFit: 'cover',
              objectPosition: 'center',
              transform: 'scaleX(-1)',
              display: 'block',
            }}
          />
        </Box>
      </Box>

      {/* Stats - Join With Us; Join in Roots & Wings for Better Tomorrow; bar #24584E 63% + blurred bg; Caveat/Nunito */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              fontWeight: 700,
              fontSize: { xs: 24, md: 27 },
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#313530',
              textAlign: 'center',
              mb: '25px',
            }}
          >
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 26, sm: 34, md: 40 },
              lineHeight: '1.25em',
              letterSpacing: '0.01em',
              color: '#2D2929',
              textAlign: 'center',
              mb: { xs: '30px', md: '50px' },
            }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(36, 88, 78, 0.63)',
                borderRadius: '23px',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
